import React, { useMemo } from 'react';
import {
  useAppContext,
  useTranslator,
  createDateObject,
  formatIsoDate,
  addDays,
  subDays,
  TrackerHistoryConnectorApollo,
} from '@front-activerse/common-components';
import Timeline from '../../../Timeline/index.jsx';
import TimelinePlaceholder from '../../../Timeline/TimelinePlaceholder.jsx';
import { Link } from 'react-router-dom';
import TrackerDayDonutChart from './TrackerDayDonutChart';

interface TrackerTimelineProps {
  date: string;
}

const DAYS_BACK = 30;
const DAYS_FORWARD = 90;

interface TrackerTimelineWithDataProps extends TrackerTimelineProps {
  trackerHistoryData?: Array<{
    date: string;
    totalCalories: number;
    consumedCalories: number;
    remainingCalories: number;
    isGoalAchieved: boolean;
  }>;
}

const TrackerTimelineWithData: React.FC<TrackerTimelineWithDataProps> = ({
  date,
  trackerHistoryData,
}) => {
  const { t } = useTranslator();
  const {
    svgInline: { LeftArrow, RightArrow },
  } = useAppContext();

  const dates = useMemo(() => {
    const datesList: Date[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = DAYS_BACK; i >= 1; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      datesList.push(d);
    }

    datesList.push(new Date(today));

    for (let i = 1; i <= DAYS_FORWARD; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      datesList.push(d);
    }

    return datesList;
  }, []);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const historyMap = useMemo(() => {
    const map = new Map<
      string,
      {
        date: string;
        totalCalories: number;
        consumedCalories: number;
        remainingCalories: number;
        isGoalAchieved: boolean;
      }
    >();
    (trackerHistoryData || []).forEach((entry) => {
      map.set(entry.date, entry);
    });
    return map;
  }, [trackerHistoryData]);

  return (
    <div className="timeline-container">
      <Timeline
        activeDate={createDateObject(date)}
        dates={dates}
        leftArrow={<LeftArrow />}
        rightArrow={<RightArrow />}
      >
        {({
          active,
          isoDate,
          linkClassName,
          date: dateObj,
        }: {
          isoDate: string;
          active: boolean;
          linkClassName: string;
          date: Date;
        }) => {
          const isToday =
            dateObj.getFullYear() === today.getFullYear() &&
            dateObj.getMonth() === today.getMonth() &&
            dateObj.getDate() === today.getDate();
          return (
            <Link
              {...(active ? { 'aria-current': 'page' } : {})}
              to={`/tracker/${isoDate}`}
              replace={true}
              className={`${linkClassName}${isToday ? ' timeline__link--today' : ''}`}
            >
              <span className="timeline-day-name">{t(`weekday${dateObj.getDay()}-short`)}</span>
              <div className="timeline-day-chart-wrapper">
                <TrackerDayDonutChart history={historyMap.get(isoDate)} />
                <span className="timeline-day-number">{dateObj.getDate()}</span>
              </div>
            </Link>
          );
        }}
      </Timeline>
    </div>
  );
};

const TrackerTimelineContent: React.FC<TrackerTimelineProps> = ({ date }) => {
  const {
    svgInline: { LeftArrow, RightArrow },
  } = useAppContext();

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const startDate = formatIsoDate(subDays(today, DAYS_BACK));
  const endDate = formatIsoDate(addDays(today, DAYS_FORWARD));

  return (
    <TrackerHistoryConnectorApollo startDate={startDate} endDate={endDate}>
      {({ loading, trackerHistoryData }) =>
        loading ? (
          <div className="timeline-container">
            <TimelinePlaceholder leftArrow={<LeftArrow />} rightArrow={<RightArrow />} />
          </div>
        ) : (
          <TrackerTimelineWithData date={date} trackerHistoryData={trackerHistoryData} />
        )
      }
    </TrackerHistoryConnectorApollo>
  );
};

export default TrackerTimelineContent;
